import { assertWorkspaceSession, workspaceSessionStamp, sessionChangedError } from 'src/auth/workspaceSession';
import { notifyAuthSessionChanged } from 'src/auth/sessionEvents';
import { toast } from 'react-toastify';
import { config } from '../config';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import {
  ADMIN_REALM_AUTH_MODE,
  ADMIN_REALM_LOGIN_PATH,
  ADMIN_REALM_LOGOUT_PATH,
  ADMIN_REALM_REFRESH_PATH,
  AdminRealmAuthContractError,
  LEGACY_ADMIN_LOGIN_PATH,
  LEGACY_ADMIN_LOGOUT_PATH,
  LEGACY_ADMIN_REFRESH_PATH,
  parseEnabledAdminRealmSession
} from '../auth/adminRealmAuth';
import {
  ADMIN_REALM_FORGOT_PASSWORD_PATH,
  ADMIN_REALM_RESET_PASSWORD_PATH,
  LEGACY_ADMIN_FORGOT_PASSWORD_PATH,
  LEGACY_ADMIN_RESET_PASSWORD_PATH,
  LEGACY_ADMIN_VERIFY_OTP_PATH
} from '../auth/adminRealmRecovery';
import {
  AdminSurfacePrincipal,
  AdminSurfaceSession,
  parseAdminSurfacePrincipal
} from '../contracts/multiCompany/v1';
// default
axios.defaults.baseURL = config.API_URL;
axios.defaults.timeout = 30_000; // 30 s — prevent hung requests from blocking the UI
// content type
axios.defaults.headers.post['Content-Type'] = 'application/json';

// Storage keys — split so the refresh token never leaks into the access-token slot.
const ACCESS_STORAGE_KEY = 'PLS';
const REFRESH_STORAGE_KEY = 'PLS_refresh';
const AUTH_ENDPOINTS = [
  '/company-invitations/preview',
  '/company-invitations/authenticate',
  '/company-invitations/accept',
  LEGACY_ADMIN_LOGIN_PATH,
  LEGACY_ADMIN_REFRESH_PATH,
  LEGACY_ADMIN_LOGOUT_PATH,
  ADMIN_REALM_LOGIN_PATH,
  ADMIN_REALM_REFRESH_PATH,
  ADMIN_REALM_LOGOUT_PATH,
  ADMIN_REALM_FORGOT_PASSWORD_PATH,
  ADMIN_REALM_RESET_PASSWORD_PATH,
  LEGACY_ADMIN_FORGOT_PASSWORD_PATH,
  LEGACY_ADMIN_VERIFY_OTP_PATH,
  LEGACY_ADMIN_RESET_PASSWORD_PATH
];

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readAccessSession = (): UnknownRecord | null => {
  try {
    const raw = localStorage.getItem(ACCESS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const readAdminRealmContext = (): {
  marked: boolean;
  principal: AdminSurfacePrincipal | null;
} => {
  const session = readAccessSession();
  if (session?.authMode !== ADMIN_REALM_AUTH_MODE) {
    return { marked: false, principal: null };
  }
  return {
    marked: true,
    principal: parseAdminSurfacePrincipal(session.principal)
  };
};

const getRequestPath = (requestUrl?: string): string => {
  if (!requestUrl) return '';
  try {
    const base =
      axios.defaults.baseURL ||
      (typeof window !== 'undefined' ? window.location.origin : undefined);
    return new URL(requestUrl, base).pathname;
  } catch {
    return requestUrl.split('?')[0] || '';
  }
};

const isAuthEndpoint = (requestUrl?: string): boolean => {
  const path = getRequestPath(requestUrl);
  return AUTH_ENDPOINTS.some(
    (endpoint) => path === endpoint || path.endsWith(`/api/v1${endpoint}`)
  );
};

const getErrorStatus = (error: unknown): number | undefined =>
  (error as AxiosError | undefined)?.response?.status;

const isDefinitiveRefreshRejection = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  // Some deployed refresh-token failures surface as 400 validation errors
  // instead of 401/403. Once /auth/refresh fails this way, the saved session
  // cannot recover, so clear it instead of leaving the UI stuck behind it.
  return (
    error instanceof AdminRealmAuthContractError ||
    status === 400 ||
    status === 401 ||
    status === 403
  );
};

const readAccessToken = (): string | null => {
  const token = readAccessSession()?.accessToken;
  return typeof token === 'string' && token.length > 0 ? token : null;
};

const readRefreshToken = (): string | null => {
  try {
    const splitRefreshToken = localStorage.getItem(REFRESH_STORAGE_KEY);
    if (splitRefreshToken) return splitRefreshToken;

    const raw = localStorage.getItem(ACCESS_STORAGE_KEY);
    if (!raw) return null;
    const legacyRefreshToken = JSON.parse(raw)?.refreshToken ?? null;
    if (legacyRefreshToken) {
      localStorage.setItem(REFRESH_STORAGE_KEY, legacyRefreshToken);
    }
    return legacyRefreshToken;
  } catch {
    return null;
  }
};

const writeAccessToken = (accessToken: string) => {
  try {
    const raw = localStorage.getItem(ACCESS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed.accessToken = accessToken;
    delete parsed.refreshToken;
    localStorage.setItem(ACCESS_STORAGE_KEY, JSON.stringify(parsed));
    notifyAuthSessionChanged();
  } catch {
    // noop — storage failures fall through to logout on the next 401.
  }
};

const writeRefreshToken = (refreshToken: string) => {
  try {
    localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
  } catch {
    // noop
  }
};

const writeAdminRealmSession = (session: AdminSurfaceSession) => {
  try {
    const current = readAccessSession() || {};
    const currentUser = isRecord(current.user) ? current.user : {};
    const currentRole = isRecord(currentUser.Role) ? currentUser.Role : {};
    const nextSession: UnknownRecord = {
      ...current,
      authMode: ADMIN_REALM_AUTH_MODE,
      principal: session.principal,
      user: {
        ...currentUser,
        id: session.principal.id,
        Role: {
          ...currentRole,
          name: session.principal.role
        }
      },
      accessToken: session.accessToken
    };
    delete nextSession.refreshToken;
    localStorage.setItem(ACCESS_STORAGE_KEY, JSON.stringify(nextSession));
    notifyAuthSessionChanged();
    writeRefreshToken(session.refreshToken);
  } catch {
    // noop — storage failures fall through to logout on the next 401.
  }
};

const clearAuthStorage = () => {
  try {
    localStorage.removeItem(ACCESS_STORAGE_KEY);
    localStorage.removeItem(REFRESH_STORAGE_KEY);
    notifyAuthSessionChanged();
  } catch {
    // noop
  }
};

/**
 * Server-side logout + local cleanup + redirect.
 * Used both by the user-initiated sign-out and by the 401 fallback when refresh fails.
 */
export const logout = async (options?: { sessionExpired?: boolean }) => {
  const logoutStamp = workspaceSessionStamp();
  const refreshToken = readRefreshToken();
  const realmContext = readAdminRealmContext();
  try {
    // Never leak a realm refresh token into the legacy endpoint when the
    // stored realm marker is present but its principal contract is invalid.
    if (!realmContext.marked || realmContext.principal) {
      const logoutPath = realmContext.principal
        ? ADMIN_REALM_LOGOUT_PATH
        : LEGACY_ADMIN_LOGOUT_PATH;
      // Use a bare axios call so this does NOT re-enter the interceptor below.
      await axios.post(logoutPath, refreshToken ? { refreshToken } : {}, {
        _skipAuthRefresh: true
      } as AxiosRequestConfig);
    }
  } catch {
    // Best-effort: a logout failure shouldn't trap the user in an authenticated state.
  } finally {
    if (workspaceSessionStamp() !== logoutStamp) return;
    clearAuthStorage();
    if (options?.sessionExpired) {
      try {
        toast.error('Your session has expired. Please sign in again.');
      } catch {
        // noop — toast may not be mounted in some flows
      }
    }
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }
};

axios.interceptors.request.use(
  (config) => {
    if (!isAuthEndpoint(config.url)) {
      const guarded = config as typeof config & { _workspaceSessionStamp?: string };
      guarded._workspaceSessionStamp = assertWorkspaceSession(guarded._workspaceSessionStamp);
    }
    const token = readAccessToken();

    // Axios' legacy JSON POST default otherwise survives on FormData requests
    // and Fastify receives an unparseable body with no multipart boundary.
    // Removing it lets the browser attach the correct boundary for every
    // Company Profile upload, including platform-scoped routes.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    }

    if (token && !isAuthEndpoint(config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Refresh-token coordination ---
//
// Multiple in-flight requests may all 401 at once. We must:
//   1. Send only ONE /auth/refresh call.
//   2. Queue the other failed requests and replay them with the new access token
//      once the refresh resolves.
//   3. If the refresh itself fails, reject every queued request and log out.

let isRefreshing = false;
type Pending = {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
};
let pendingQueue: Pending[] = [];

const flushQueue = (error: unknown, token: string | null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error || !token) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

export const refreshSessionAccessToken = async (): Promise<string> => {
  const refreshStamp = workspaceSessionStamp();
  const refreshToken = readRefreshToken();
  if (!refreshToken) {
    if (readAccessToken()) await logout({ sessionExpired: true });
    throw new Error('Your session has expired. Please sign in again.');
  }
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => pendingQueue.push({ resolve, reject }));
  }
  isRefreshing = true;
  try {
      const realmContext = readAdminRealmContext();
      if (realmContext.marked && !realmContext.principal) {
        throw new AdminRealmAuthContractError(
          'Stored admin realm principal is invalid.'
        );
      }
      const refreshPath = realmContext.principal
        ? ADMIN_REALM_REFRESH_PATH
        : LEGACY_ADMIN_REFRESH_PATH;
      const refreshResponse = await axios.post(refreshPath, { refreshToken }, {
        _skipAuthRefresh: true
      } as AxiosRequestConfig);

      if (workspaceSessionStamp() !== refreshStamp) throw sessionChangedError();
      let newAccessToken: string;
      if (realmContext.principal) {
        const realmSession = parseEnabledAdminRealmSession(
          refreshResponse.data
        );
        if (realmSession.principal.id !== realmContext.principal.id) {
          throw new AdminRealmAuthContractError(
            'Admin realm refresh changed the authenticated principal.'
          );
        }
        writeAdminRealmSession(realmSession);
        newAccessToken = realmSession.accessToken;
      } else {
        const data = refreshResponse?.data?.data ?? refreshResponse?.data ?? {};
        const legacyAccessToken: string | undefined = data.accessToken;
        const legacyRefreshToken: string | undefined = data.refreshToken;
        if (!legacyAccessToken) {
          throw new Error('Refresh response missing accessToken');
        }
        writeAccessToken(legacyAccessToken);
        if (legacyRefreshToken) writeRefreshToken(legacyRefreshToken);
        newAccessToken = legacyAccessToken;
      }

    flushQueue(null, newAccessToken);
    return newAccessToken;
  } catch (error) {
    flushQueue(error, null);
    if (workspaceSessionStamp() === refreshStamp && isDefinitiveRefreshRejection(error)) await logout({ sessionExpired: true });
    throw error;
  } finally {
    isRefreshing = false;
  }
};

/**
 * Server-authored 5xx bodies are not safe to put on screen. A failed Prisma
 * write, for instance, returns the whole invocation trace including the
 * server's absolute source path, the constraint name and the failing row
 * values — all of which the admin UI rendered verbatim (refs #1656, F2).
 *
 * 4xx bodies are the API's deliberate user-facing text ("Application status is
 * invalid") and pass through untouched, unless they carry something that only
 * a server stack trace would contain.
 */
const SERVER_DETAIL_PATTERNS = [
  /\/(?:opt|home|usr|var|srv)\//,
  /\bat\s+[A-Za-z0-9_$.]+\s*\(/,
  /\b(?:PrismaClient|prisma\.|tx\.|ConnectorError|PostgresError|SQLSTATE)\b/i,
  /\binvocation in\b/i
];

export const GENERIC_API_ERROR_MESSAGE =
  'Something went wrong, please try again.';

export const safeApiErrorMessage = (
  status: number | undefined,
  rawMessage: unknown
): string => {
  const message = typeof rawMessage === 'string' ? rawMessage.trim() : '';
  if (!message) return GENERIC_API_ERROR_MESSAGE;
  if (status !== undefined && status >= 500) return GENERIC_API_ERROR_MESSAGE;
  if (SERVER_DETAIL_PATTERNS.some((pattern) => pattern.test(message))) {
    return GENERIC_API_ERROR_MESSAGE;
  }
  return message;
};

axios.interceptors.response.use(
  (response) => {
    if (!isAuthEndpoint(response.config.url)) {
      assertWorkspaceSession((response.config as AxiosRequestConfig & { _workspaceSessionStamp?: string })._workspaceSessionStamp);
    }
    return response;
  },
  async (error: AxiosError) => {
    if (axios.isCancel(error)) return Promise.reject(error);
    if (error.config && !isAuthEndpoint(error.config.url)) {
      assertWorkspaceSession((error.config as AxiosRequestConfig & { _workspaceSessionStamp?: string })._workspaceSessionStamp);
    }
    const status = error?.response?.status;
    const originalRequest = error?.config as
      | (AxiosRequestConfig & { _retry?: boolean; _skipAuthRefresh?: boolean })
      | undefined;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest._skipAuthRefresh ||
      isAuthEndpoint(originalRequest.url)
    ) {
      const body = error?.response?.data as
        | { message?: unknown; error?: { message?: unknown } }
        | undefined;
      const rawMessage =
        (typeof body?.message === 'string' && body.message) ||
        (typeof body?.error?.message === 'string' && body.error.message) ||
        undefined;
      const message = safeApiErrorMessage(status, rawMessage);
      // Engineers still get the original; only the screen is sanitised.
      console.error('API Error:', rawMessage ?? message);
      if (body && typeof body === 'object' && rawMessage !== message) {
        // Rewrite the body in place so the 50-odd call sites that read
        // `response.data.message` cannot surface server internals.
        (body as { message?: unknown }).message = message;
        if (body.error && typeof body.error === 'object') {
          (body.error as { message?: unknown }).message = message;
        }
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    const newAccessToken = await refreshSessionAccessToken();
    originalRequest.headers = {
      ...(originalRequest.headers || {}),
      Authorization: `Bearer ${newAccessToken}`
    };
    return axios(originalRequest);
  }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
// const setAuthorization = (token) => {
//   axios.defaults.headers.common["Authorization"] = "Bearer " + token;
// };

class APIClient {
  /**
   * Fetches data from given url
   */

  //  get = (url, params) => {
  //   return axios.get(url, params);
  // };
  get = (url, params) => {
    let response;

    let paramKeys = [];

    if (params) {
      Object.keys(params).map((key) => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString =
        paramKeys && paramKeys.length ? paramKeys.join('&') : '';
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }
    return response;
    // return axios.get(url, { params });
  };
  /**
   * post given data to url
   */

  create = (url, data, headers) => {
    return axios.post(url, data, headers);
  };
  /**
   * Updates data
   */

  update = (url, data) => {
    return axios.put(url, data);
  };

  /**
   * Partial update — used by endpoints that follow REST conventions and
   * expect PATCH for transitional state changes (e.g. incident status).
   */

  patch = (url, data) => {
    return axios.patch(url, data);
  };

  /**
   * Delete
   */

  delete = (url, params) => {
    return axios.delete(url, { params });
  };
}
const getLoggedinUser = () => {
  const user = localStorage.getItem('berkshire');

  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

export { APIClient, getLoggedinUser, ACCESS_STORAGE_KEY, REFRESH_STORAGE_KEY };
